<script>
import Banner from 'dashboard/components/ui/Banner.vue';
import { mapGetters } from 'vuex';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - two-step-verification.UI.5
import { useKaptionTwoStep } from 'dashboard/composables/useKaptionTwoStep';

// NOTICE: KAPTION-INTEGRATION
// Kaption2FA warning banner — shown until the user verifies a WhatsApp
// number. Warning-only: login is untouched; connecting the Rumo MCP is what
// actually requires the verified number.
//
// two-step-verification.UI.5 — when the multi-method feature is on (flag AND
// permission), "verified" means ANY verified second-step method, and an
// administrator who must set one up sees the deadline instead. With the
// feature off this behaves exactly as before.
export default {
  components: { Banner },
  setup() {
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    const twoStep = useKaptionTwoStep();
    return { isKaptionFlagEnabled, twoStep };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      globalConfig: 'globalConfig/get',
    }),
    twoStepMode() {
      return this.twoStep.isEnabled.value;
    },
    shouldShowBanner() {
      // NOTICE: KAPTION-INTEGRATION - Dev-only: the server sets KAPTION_SKIP_2FA_NUDGE
      // ONLY when Rails.env.development? (see DashboardController#skip_2fa_nudge?), so
      // this is a no-op in production. It hides the nudge banner and nothing else —
      // enrollment, verification and MCP session binding are untouched.
      if (this.globalConfig?.kaptionSkip2faNudge) return false;

      if (this.twoStepMode) return this.shouldShowTwoStepBanner;

      if (!this.isKaptionFlagEnabled('KAPTION_WHATSAPP_2FA')) return false;

      // Field is absent on older payloads → undefined → banner stays hidden.
      return this.currentUser.kaption_whatsapp_2fa_verified === false;
    },
    shouldShowTwoStepBanner() {
      const status = this.twoStep.status.value;
      if (!status || !this.twoStep.isActive.value) return false;
      if (this.twoStep.hasFactor.value) return false;
      // Administrators who must set one up always see it; everyone else only
      // where the WhatsApp nudge used to be on.
      return (
        this.twoStep.isRequired.value ||
        this.isKaptionFlagEnabled('KAPTION_WHATSAPP_2FA')
      );
    },
    bannerMessage() {
      if (!this.twoStepMode) return this.$t('KAPTION_WA2FA.BANNER.MESSAGE');
      if (this.twoStep.isBlocked.value)
        return this.$t('KAPTION_TWO_STEP.BANNER.BLOCKED');
      if (this.twoStep.isRequired.value && this.twoStep.graceEndsAt.value)
        return this.$t('KAPTION_TWO_STEP.BANNER.MESSAGE', {
          date: this.formatDate(this.twoStep.graceEndsAt.value),
        });
      return this.$t('KAPTION_TWO_STEP.BANNER.NUDGE');
    },
    actionLabel() {
      return this.twoStepMode
        ? this.$t('KAPTION_TWO_STEP.BANNER.ACTION')
        : this.$t('KAPTION_WA2FA.BANNER.ACTION');
    },
  },
  watch: {
    twoStepMode: {
      immediate: true,
      handler(enabled) {
        if (enabled) this.twoStep.fetchStatus();
      },
    },
  },
  methods: {
    formatDate(date) {
      const locale = String(this.$i18n?.locale || 'en').replace('_', '-');
      return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
      }).format(date);
    },
    goToSettings() {
      this.$router.push({
        name: this.twoStepMode
          ? 'profile_settings_index'
          : 'profile_settings_whatsapp_2fa',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShowBanner"
    color-scheme="warning"
    :banner-message="bannerMessage"
    :action-button-label="actionLabel"
    action-button-icon="i-lucide-shield-check"
    has-action-button
    @primary-action="goToSettings"
  />
</template>
